import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly, TeamHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { MLProjectTranslation, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectTranslationStatus, SortDirection } from '@/utils/enums';

import TableRow from './components/TableRow';

const StatusControls = styled(Controls)`
  display: inline-flex;
  margin: 0 0 12px;
`;

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: top;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const MLLanguage: FC<{ status: MLProjectTranslationStatus }> = ({ status }) => {
  const { language } = useParams();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const navigate = useNavigate();
  const { sort, setSort, sortQueryParamObj } = useSortState({
    direction: SortDirection.DESC,
    property: 'createdAt',
  });

  const fetchTranslations = ({
    pageParam = '/v1/distribution/ml-translations',
  }) => {
    return api.get<never, PaginatedResponse<MLProjectTranslation>>(pageParam, {
      params: {
        ...sortQueryParamObj,
        'filter[status]': status,
        'filter[language]': language,
        ...(!!debouncedSearch && {
          'filter[search]': debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'ml_translations',
      {
        status,
        language,
        sort,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    fetchTranslations,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 90 * 1000,
    },
  );

  const translations = useMemo(
    () =>
      data?.pages?.reduce<MLProjectTranslation[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  return (
    <>
      <TeamHeading />
      <PageHeading>{language}</PageHeading>
      <StatusControls>
        <ControlsButton
          active={status === MLProjectTranslationStatus.PENDING}
          onClick={() => navigate(`../pending`, { relative: 'path' })}
        >
          Pending
        </ControlsButton>
        <ControlsButton
          active={status === MLProjectTranslationStatus.DONE}
          onClick={() => navigate(`../done`, { relative: 'path' })}
        >
          Done
        </ControlsButton>
      </StatusControls>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by title"
      />
      <ScrollableTable>
        <StyledTable $stickyActions>
          <thead>
            <tr>
              <Th>Thumb & Title</Th>
              <Th>Status</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="createdAt"
                title="Date Added"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="updatedAt"
                title="Date Modified"
              />
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!translations?.length}
            >
              {translations?.map((t) => (
                <TableRow key={t.id} translation={t} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default MLLanguage;
